import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Badge, Button } from "@/design-system/components/ui";
import type { BadgeProps } from "@/design-system/components/ui";
import { cn } from "@/design-system/lib/utils";
import { DataTable, type Column } from "@/components/layout/DataTable";
import { StagedSubmissionReviewDrawer } from "@/components/Collection/StagedSubmissionReviewDrawer";
import { NewDocumentRequestModal } from "@/components/portalAbsorption/NewDocumentRequestModal";
import { displayEmail } from "@/design-system/lib/format";
import { formatCalendarDate, useDateFormatter } from "@/utils/datetime";
import {
  collectionStagingApi,
  type AssessmentRunStatus,
  type ScanStatus,
  type StagedSubmissionListItem,
  type StagingListQuery,
} from "@/api/collectionStaging";
import { collectionStagingKeys } from "@/api/collectionStagingKeys";
import { portalAccountsApi } from "@/api/portalAccounts";
import type { CollectionPackageQueueItem } from "@/api/collectionPackages";
import type { BrokerType } from "@/api/brokers";
import { portalAbsorptionKeys } from "@/hooks/portalAbsorption/portalAbsorptionKeys";
import { reviewRowStatus } from "@/components/Collection/collectionStatusLabels";

// Portal-absorption spine (WS-C0), overhauled in §2.4 — the combined
// "Review Queue + Document Requests" tab, mounted per party / vendor /
// broker / brokerage / TPS. Entity-surface principle: staff never leave
// the entity page — rows open the SAME shared StagedSubmissionReviewDrawer
// used on /collection/staging (here with the queue-management actions and
// the full-queue escape hatch hidden), and "New document request" opens an
// in-place popup instead of deep-linking the composer. The drawer's
// approve/file/return/reopen mutations invalidate the ["collection-staging"]
// and ["portal-account-packages"] prefixes, which cover this tab's queries.

type BadgeStatus = NonNullable<BadgeProps["status"]>;

const SCAN_STATUS: Record<ScanStatus, { label: string; status: BadgeStatus }> = {
  Pending: { label: "Scanning", status: "info" },
  Clean: { label: "Clean", status: "finalized" },
  Infected: { label: "Infected", status: "error" },
  Error: { label: "Scan error", status: "error" },
};

const ASSESSMENT_STATUS: Record<AssessmentRunStatus, { label: string; status: BadgeStatus }> = {
  Pending: { label: "AI pending", status: "info" },
  Completed: { label: "AI ready", status: "finalized" },
  Failed: { label: "AI failed", status: "error" },
  Skipped: { label: "AI skipped", status: "draft" },
};

/** Scope by the linked portal account or by package owner. */
export type ReviewRequestsScope =
  | { portalUserId: string }
  | { ownerEntityType: string; ownerEntityId: string };

export interface AccountReviewRequestsTabProps {
  scope: ReviewRequestsScope;
  /** Wire the type-aware "New document request" popup; omit to hide.
   *  `owner` = the hosting entity's own record — it becomes the created
   *  package's owner (the review-time filing default, C1). */
  requestComposer?: {
    accountType: string;
    /** Broker sub-type — only the broker mount supplies it, so a Junior
     *  broker's composer defaults to the `JuniorBroker` catalog (provisioning
     *  stays `Broker`). Undefined elsewhere → behaviour unchanged. */
    brokerType?: BrokerType;
    email?: string | null;
    owner?: { ownerEntityType: string; ownerEntityId: string } | null;
  };
  emptyMessage?: string;
}

/** Mutually-exclusive filter tiles (StatCard-as-button, radio behavior). */
type FilterTile = "awaiting" | "overdue" | "returned" | "pending" | "filed";

/** Unified list row: a staging submission OR a synthesized pending-recipient
 *  package row (packages whose recipient hasn't finished submitting — they
 *  have no staged submission yet). F8 — clicking a pending row opens the shared
 *  drawer in package-view mode (what was sent + where each item stands). */
type UnifiedRow =
  | { kind: "staging"; staging: StagedSubmissionListItem }
  | { kind: "pending"; pkg: CollectionPackageQueueItem };

/** Items whose next move is the recipient's. The server's
 *  itemsAwaitingRecipient counts only those (Pending, InProgress,
 *  ChangesRequested); items waiting on staff review are not the recipient's to
 *  move. An older payload without the field falls back to "not completed". */
function itemsAwaitingRecipient(p: CollectionPackageQueueItem): number {
  return p.itemsAwaitingRecipient ?? p.itemsTotal - p.itemsCompleted;
}

/** Overdue mirrors the documented server definition (portalAccounts.ts):
 *  AwaitingReview + parent package past due. Terminal package states never
 *  reach AwaitingReview rows in practice; the due-date check is the filter. */
function isOverdueRow(s: StagedSubmissionListItem): boolean {
  return (
    s.reviewStatus === "AwaitingReview" &&
    s.packageDueDate != null &&
    new Date(s.packageDueDate).getTime() < Date.now()
  );
}

interface StatTileProps {
  label: string;
  value: number;
  active: boolean;
  onClick: () => void;
  testId: string;
  subtitle?: string;
}

// Mirrors the clickable StatCard pattern from PortalAccountsPage (radio
// behavior owned by the host: click again to clear).
function StatTile({ label, value, active, onClick, testId, subtitle }: StatTileProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      aria-pressed={active}
      className={cn(
        "text-left border rounded-md p-space-3 bg-white-pure transition-colors",
        "hover:bg-white-warm focus:outline-none focus-visible:shadow-goldRing",
        active ? "border-gold ring-2 ring-gold" : "border-border-light",
      )}
    >
      <div className="text-caption uppercase tracking-wide text-gray-mid">{label}</div>
      <div className="font-display text-h3 text-onyx">{value}</div>
      {subtitle && <div className="text-caption text-gray-mid">{subtitle}</div>}
    </button>
  );
}

export function AccountReviewRequestsTab({
  scope,
  requestComposer,
  emptyMessage = "Nothing has been submitted for review yet.",
}: AccountReviewRequestsTabProps) {
  const { date } = useDateFormatter();
  const [reviewingId, setReviewingId] = useState<string | null>(null);
  // F8 — the pending-recipient package opened in the drawer's package-view
  // mode (mutually exclusive with reviewingId; opening one clears the other).
  const [pendingPkg, setPendingPkg] = useState<CollectionPackageQueueItem | null>(null);
  // F7 deep-links pre-select a filter tile via ?rqTile= (read once at mount —
  // the tile stays local radio state afterwards; the param is never written).
  const [searchParams] = useSearchParams();
  const [activeTile, setActiveTile] = useState<FilterTile | null>(() => {
    const raw = searchParams.get("rqTile");
    return raw === "awaiting" || raw === "overdue" || raw === "returned"
      || raw === "pending" || raw === "filed"
      ? raw
      : null;
  });
  const [composerOpen, setComposerOpen] = useState(false);

  const byAccount = "portalUserId" in scope;
  const queryKey = byAccount
    ? collectionStagingKeys.byAccount(scope.portalUserId)
    : collectionStagingKeys.byOwner(scope.ownerEntityType, scope.ownerEntityId);
  const listParams: StagingListQuery = {
    ...(byAccount
      ? { portalUserId: scope.portalUserId }
      : { ownerEntityType: scope.ownerEntityType, ownerEntityId: scope.ownerEntityId }),
    // Full review history (awaiting + reviewed) — a per-record tab that
    // empties after review would read as broken; the status column makes
    // awaiting items obvious.
    includeReviewed: true,
    sortBy: "CreatedAt",
    sortDescending: true,
    limit: 100,
  };

  const queue = useQuery({
    queryKey,
    queryFn: () => collectionStagingApi.list(listParams),
  });

  // Header aggregate + per-package roll-ups exist per portal ACCOUNT only —
  // there is no per-owner aggregate endpoint, so owner-scoped mounts hide the
  // header and the "Pending recipient" tile (they degrade to the staging
  // rows + the four staging tiles).
  const summary = useQuery({
    queryKey: collectionStagingKeys.accountSummary(byAccount ? scope.portalUserId : ""),
    queryFn: () => portalAccountsApi.submissionSummary((scope as { portalUserId: string }).portalUserId),
    enabled: byAccount,
  });
  const packages = useQuery({
    queryKey: portalAbsorptionKeys.accountPackages(byAccount ? scope.portalUserId : ""),
    queryFn: () => portalAccountsApi.listPackages((scope as { portalUserId: string }).portalUserId),
    enabled: byAccount,
  });

  const rows = queue.data?.items ?? [];
  const packageRows = useMemo(
    () => (byAccount ? packages.data ?? [] : []),
    [byAccount, packages.data],
  );

  // Tile counts, computed from the visible rows (the tiles filter this list).
  const awaitingRows = useMemo(
    () => rows.filter((s) => s.reviewStatus === "AwaitingReview"),
    [rows],
  );
  const overdueRows = useMemo(() => rows.filter(isOverdueRow), [rows]);
  const returnedRows = useMemo(
    () => rows.filter((s) => s.reviewStatus === "Returned"),
    [rows],
  );
  const filedRows = useMemo(
    () => rows.filter((s) => s.filedDocumentId != null),
    [rows],
  );
  const pendingPackages = useMemo(
    () => packageRows.filter((p) => itemsAwaitingRecipient(p) > 0),
    [packageRows],
  );
  const oldestWaitDays = awaitingRows.reduce(
    (max, s) => Math.max(max, s.reviewWaitDays ?? 0),
    0,
  );

  // Header progress = Σ itemsCompleted / Σ itemsTotal over the account's
  // outstanding packages.
  const itemsTotal = packageRows.reduce((sum, p) => sum + p.itemsTotal, 0);
  const itemsCompleted = packageRows.reduce((sum, p) => sum + p.itemsCompleted, 0);
  const progressPct = itemsTotal > 0 ? Math.round((itemsCompleted / itemsTotal) * 100) : 0;

  const toggleTile = (tile: FilterTile) =>
    setActiveTile((prev) => (prev === tile ? null : tile));

  const unifiedRows: UnifiedRow[] = useMemo(() => {
    const stagingFiltered: StagedSubmissionListItem[] =
      activeTile === null
        ? rows
        : activeTile === "awaiting"
          ? awaitingRows
          : activeTile === "overdue"
            ? overdueRows
            : activeTile === "returned"
              ? returnedRows
              : activeTile === "filed"
                ? filedRows
                : [];
    const pending: CollectionPackageQueueItem[] =
      activeTile === null || activeTile === "pending" ? pendingPackages : [];
    return [
      ...stagingFiltered.map((staging) => ({ kind: "staging", staging }) as const),
      ...pending.map((pkg) => ({ kind: "pending", pkg }) as const),
    ];
  }, [activeTile, rows, awaitingRows, overdueRows, returnedRows, filedRows, pendingPackages]);

  const columns: Column<UnifiedRow>[] = [
    {
      key: "request",
      header: "Request",
      render: (r) =>
        r.kind === "staging" ? (
          <div className="flex flex-col gap-space-1">
            <span className="font-semibold text-onyx">{r.staging.templateName}</span>
            <span className="text-caption text-gray-mid">
              {r.staging.originalFileName}
              {r.staging.documentTagCode ? ` · ${r.staging.documentTagCode}` : ""}
            </span>
          </div>
        ) : (
          <div className="flex flex-col gap-space-1">
            <span className="font-medium text-gray-mid">
              {itemsAwaitingRecipient(r.pkg)} item
              {itemsAwaitingRecipient(r.pkg) === 1 ? "" : "s"} awaiting recipient
            </span>
            <span className="text-caption text-gray-mid">
              {r.pkg.recipientRoleLabel ? `${r.pkg.recipientRoleLabel} · ` : ""}
              {r.pkg.itemsCompleted}/{r.pkg.itemsTotal} completed
            </span>
          </div>
        ),
    },
    {
      key: "recipient",
      header: "From",
      mobileHidden: true,
      render: (r) => {
        const name = r.kind === "staging" ? r.staging.recipientName : r.pkg.recipientFullName;
        const email = r.kind === "staging" ? r.staging.recipientEmail : r.pkg.recipientEmail;
        return (
          <div className="flex flex-col gap-space-1">
            <span className="text-onyx">{name}</span>
            <span className="text-caption text-gray-mid">{displayEmail(email)}</span>
          </div>
        );
      },
    },
    {
      key: "review",
      header: "Status",
      render: (r) => {
        if (r.kind === "pending") {
          return <Badge status="draft">Pending recipient</Badge>;
        }
        // Review loop — the owner-approved lifecycle STATUS (L1–L6), composed
        // from the review status, the item's status and the resubmitted /
        // reopened marker; "Filed" / "Not filed" is part of the Approved label.
        const st = reviewRowStatus(r.staging);
        return <Badge status={st.tone}>{st.label}</Badge>;
      },
    },
    {
      key: "checks",
      header: "Checks",
      mobileHidden: true,
      render: (r) => {
        if (r.kind === "pending") {
          return <span className="text-caption text-gray-mid">—</span>;
        }
        const s = r.staging;
        const scan = s.scanOverriddenAt
          ? { label: "Scan overridden", status: "review" as BadgeStatus }
          : SCAN_STATUS[s.scanStatus];
        const ai = ASSESSMENT_STATUS[s.assessmentStatus];
        return (
          <div className="flex flex-wrap items-center gap-space-1">
            <Badge status={scan.status}>{scan.label}</Badge>
            <Badge status={ai.status}>{ai.label}</Badge>
          </div>
        );
      },
    },
    {
      key: "submitted",
      header: "Submitted",
      render: (r) =>
        r.kind === "staging" ? (
          <div className="flex flex-col gap-space-1">
            <span className="text-caption text-gray-mid">{date(r.staging.createdAt)}</span>
            {r.staging.reviewStatus === "AwaitingReview" &&
              typeof r.staging.reviewWaitDays === "number" &&
              r.staging.reviewWaitDays > 0 && (
                <span className="text-caption text-gray-mid">
                  Waiting {r.staging.reviewWaitDays}d
                </span>
              )}
          </div>
        ) : (
          <span className="text-caption text-gray-mid">
            {r.pkg.dueDate ? `Due ${formatCalendarDate(r.pkg.dueDate)}` : "—"}
          </span>
        ),
    },
  ];

  return (
    <div className="flex flex-col gap-space-3">
      {/* Header — account-scoped aggregate only (no per-owner endpoint). */}
      {byAccount && summary.data && (
        <div
          className="flex flex-col gap-space-2 rounded-md border border-border-light bg-white-pure p-space-3"
          data-testid="review-requests-header"
        >
          <div className="flex items-center justify-between gap-space-3">
            <span className="text-small font-semibold text-onyx">
              {summary.data.packageCount} package{summary.data.packageCount === 1 ? "" : "s"} ·{" "}
              {summary.data.totalItemCount} item{summary.data.totalItemCount === 1 ? "" : "s"}
            </span>
            {itemsTotal > 0 && (
              <span className="text-caption text-gray-mid">
                {itemsCompleted}/{itemsTotal} items completed
              </span>
            )}
          </div>
          <div
            className="h-1.5 w-full overflow-hidden rounded-full bg-border-light"
            role="progressbar"
            aria-valuenow={progressPct}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label="Items completed"
          >
            <div className="h-full bg-gold" style={{ width: `${progressPct}%` }} />
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-start justify-between gap-space-3">
        <div
          className={cn(
            "grid flex-1 grid-cols-2 gap-space-2",
            byAccount ? "sm:grid-cols-5" : "sm:grid-cols-4",
          )}
        >
          <StatTile
            label="Awaiting your review"
            value={awaitingRows.length}
            active={activeTile === "awaiting"}
            onClick={() => toggleTile("awaiting")}
            testId="review-tile-awaiting"
            subtitle={
              awaitingRows.length > 0 && oldestWaitDays > 0
                ? `oldest waiting ${oldestWaitDays}d`
                : undefined
            }
          />
          <StatTile
            label="Overdue"
            value={overdueRows.length}
            active={activeTile === "overdue"}
            onClick={() => toggleTile("overdue")}
            testId="review-tile-overdue"
          />
          <StatTile
            label="Returned"
            value={returnedRows.length}
            active={activeTile === "returned"}
            onClick={() => toggleTile("returned")}
            testId="review-tile-returned"
          />
          {byAccount && (
            <StatTile
              label="Pending recipient"
              value={pendingPackages.length}
              active={activeTile === "pending"}
              onClick={() => toggleTile("pending")}
              testId="review-tile-pending"
            />
          )}
          <StatTile
            label="Filed"
            value={filedRows.length}
            active={activeTile === "filed"}
            onClick={() => toggleTile("filed")}
            testId="review-tile-filed"
          />
        </div>
        {requestComposer && (
          <Button
            variant="secondary"
            onClick={() => setComposerOpen(true)}
            data-testid="new-document-request"
          >
            New document request
          </Button>
        )}
      </div>

      <DataTable
        columns={columns}
        rows={unifiedRows}
        loading={queue.isLoading}
        error={queue.isError ? "Could not load the review queue." : null}
        rowKey={(r) => (r.kind === "staging" ? r.staging.id : `pending-${r.pkg.id}`)}
        emptyMessage={emptyMessage}
        // Staging rows open the review panel; F8 — pending-recipient rows open
        // the same drawer in package-view mode. Opening one clears the other.
        onRowClick={(r) => {
          if (r.kind === "staging") {
            setPendingPkg(null);
            setReviewingId(r.staging.id);
          } else {
            setReviewingId(null);
            setPendingPkg(r.pkg);
          }
        }}
        rowTestId={(r) =>
          r.kind === "staging"
            ? `account-review-row-${r.staging.id}`
            : `account-pending-row-${r.pkg.id}`
        }
        rowClassName={(r) =>
          r.kind === "pending"
            ? "[&>td]:py-space-3 bg-white-warm"
            : "[&>td]:py-space-3"
        }
      />

      {/* Same review drawer as /collection/staging, opened in place; on the
          entity surface the queue-management actions + the full-queue escape
          hatch are hidden so review never leaves the entity. Its review
          mutations invalidate the ["collection-staging"] prefix (this tab's
          query AND the needs-attention badge summary) and the
          ["portal-account-packages"] prefix (the pending rows + header). */}
      <StagedSubmissionReviewDrawer
        submissionId={reviewingId}
        row={
          reviewingId
            ? rows.find((r) => r.id === reviewingId) ?? null
            : null
        }
        packageRow={pendingPkg}
        accountId={byAccount ? scope.portalUserId : null}
        onClose={() => {
          setReviewingId(null);
          setPendingPkg(null);
        }}
        hideFullQueueLink
        hideQueueActions
      />

      {requestComposer && (
        <NewDocumentRequestModal
          open={composerOpen}
          onClose={() => setComposerOpen(false)}
          accountType={requestComposer.accountType}
          brokerType={requestComposer.brokerType}
          email={requestComposer.email}
          portalUserId={byAccount ? scope.portalUserId : null}
          owner={requestComposer.owner ?? null}
          fallbackOwner={
            byAccount
              ? null
              : {
                  ownerEntityType: scope.ownerEntityType,
                  ownerEntityId: scope.ownerEntityId,
                }
          }
        />
      )}
    </div>
  );
}
